import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createIdbStorage } from '../utils/idbStorage';

// Persistance de la CONVERSATION seule (cf. `partialize` en bas de fichier).
//
// IndexedDB, jamais localStorage : une conversation porte les images générées en
// base64, et le quota de 5 Mo serait dépassé dès la deuxième image.
//
// `objets: true` — l'adaptateur reçoit l'objet d'état, PAS une chaîne JSON, et
// n'est donc pas enveloppé dans `createJSONStorage` plus bas.
//
// La raison est mesurée. Zustand appelle sa fonction d'écriture à chaque
// `set()`, sans comparer quoi que ce soit, et `createJSONStorage` sérialise
// AVANT de nous passer la main : le regroupement à 400 ms n'épargnait donc que
// l'écriture dans IndexedDB, jamais le `JSON.stringify`. Sur une conversation
// réaliste — quatorze images de référence en base64, soit 12,25 Mo persistés —
// ce `stringify` coûte 8,3 ms de fil principal, à CHAQUE caractère tapé, pour un
// résultat jeté 400 ms plus tard. D'où les ralentissements à la frappe.
//
// En mode objet, c'est IndexedDB qui sérialise, par clonage structuré, une seule
// fois par salve de 400 ms (4,0 ms mesurés). Le coût par frappe devient nul.
const chatStorage = createIdbStorage({ debounceMs: 400, objets: true });

// Plafond de la file de détourage. Dix fichiers de plusieurs mégaoctets chacun
// vivent en mémoire le temps du lot : au-delà, l'onglet devient lourd sans que
// le gain soit évident.
export const MAX_FICHIERS_CUTOUT = 10;

export const useAIStudioStore = create(persist((set, get) => ({
  // ── Panel state ──
  isOpen: false,
  // Vue affichée dans le dock de droite quand le Studio I.A est ouvert :
  // 'studio' = le panneau Studio I.A, 'inspector' = l'inspecteur classique.
  // Permet de basculer sans fermer le Studio (qui reste "actif").
  view: 'studio',
  activeTab: 'inspiration', // inspiration | i2i | inpaint | outpaint | cutout | vectorize | analyze | gallery

  // ── Chat (conversational AI assistant) ──
  chat: {
    messages: [],           // [{role, content, images?: [{job_id, prompt, url, status}], folder?: {id,name}, id}]
    isSending: false,       // waiting for LLM response
    input: '',              // current draft in the input textarea
    conversationImageCount: 0, // how many images generated this session
    remainingToday: null,   // fetched from /api/ai/chat/quota ; -1 = sans limite
    // Plafond journalier du compte, LU AU SERVEUR — il peut être propre à
    // l'utilisateur. `null` = sans limite. Ne jamais le remplacer par une
    // constante côté client : le compteur afficherait un dénominateur faux.
    maxPerDay: null,
    mode: 'generation',     // conservé pour compat backend ; l'UI est désormais unifiée (un seul assistant)
    // ── Réglages de l'interface unifiée ──
    // Images de référence (jusqu'à 4) — { b64, name }. Persistent tant que
    // l'utilisateur ne les retire pas ; transmises au modèle (vision) ET à la
    // génération (i2i).
    refImages: [],
    // Nombre d'images à générer par demande (1-4). Défaut 1 (choix utilisateur).
    imageCount: 1,
    // Ratio d'image souhaité (l'un des ratios natifs du modèle).
    aspectRatio: '1:1',
    // Hauteur de la zone de lecture, en pixels, réglée à la poignée.
    // `null` = hauteur automatique (le reste de la place disponible).
    hauteurMessages: null,
    // Résolution de sortie : '512' | '1K' (défaut) | '2K' | '4K'.
    // Le 512 n'existe que sur le moteur « image ».
    resolution: '1K',
    // Moteur d'image, nommé par ce qu'il SOIGNE et non par son modèle :
    //   'texte' → rendu typographique le plus fidèle (défaut : une étiquette
    //             porte presque toujours du texte destiné à l'impression) ;
    //   'image' → plus rapide, moins cher, 512 px et ratios panoramiques.
    moteur: 'texte',
    // Niveau de raisonnement du moteur d'image. MESURÉ EN PRODUCTION : « high »
    // n'est PAS plus lent que « minimal » (23-26 s en 1K dans les deux cas), et
    // les deux sont deux fois plus rapides que de ne rien préciser (43-49 s).
    // D'où « high » par défaut : le plus soigné coûte le même temps.
    raisonnement: 'high',
    // Fond gris studio uniforme ajouté aux prompts générés — facilite le
    // détourage ultérieur via l'onglet Cutout.
    cutoutBg: false,
    // Quick-preview cache. Persists generated images across AI Studio tab
    // switches (PromptBlock is unmounted when user navigates to gallery etc).
    // Key = `${messageId}::${promptValue}` so two PromptBlocks in the same
    // message don't collide. Value = { image_b64, width, height, size_label, savedToGallery }
    previewCache: {},
    // Most-recent quick preview image_b64 (data URL). Auto-fed as reference
    // image on the NEXT quick-preview so iterations across different prompts
    // stay visually anchored to the last result (user request 2026-05-12).
    // Cleared on Nouvelle conv / mode switch.
    lastQuickPreview: null,
  },

  // ── Analyze (vision → prompt extraction) settings ──
  analyze: {
    imageB64: null,
    imageName: '',
    imageWidth: 0,        // dimensions natives de l'image uploadee (px)
    imageHeight: 0,
    includeText: false,   // true = inclure le texte/logos dans l'analyse
    richness: 2,          // 1 = minimal, 2 = compact, 3 = equilibre, 4 = detaille, 5 = exhaustif
    extractedPrompt: '',
    isExtracting: false,
    error: null,
  },

  // ── Current AI job tracking ──
  currentJobId: null,
  currentJobStatus: 'idle', // idle | queued | running | completed | failed
  currentProgress: 0,
  currentStatusLog: '',
  currentError: null,
  currentResultUrl: null,
  currentResultB64: null,
  currentResultTab: null,  // Which tab generated the current result

  // ── Inspiration / T2I settings ──
  // Défauts revus le 2026-09-17 (avant la formation) : le planificateur « beta »
  // donne plus de détail à FLUX dev à nombre de pas égal ; 24 pas suffisent en T2I.
  inspiration: {
    prompt: '',
    width: 1024,
    height: 1024,
    steps: 24,
    seed: -1,
    sampler: 'euler',
    scheduler: 'beta',
    guidance: 3.5,
    removeBg: false,
    // Fond gris studio uniforme injecté dans le prompt — prépare l'image au
    // détourage (Cutout/rembg accroche mieux sur un fond neutre uni).
    cutoutBg: false,
  },

  // ── Image-to-Image settings ──
  i2i: {
    prompt: '',
    inputImageB64: null,
    inputImageName: '',
    steps: 24,
    denoise: 0.6,
    seed: -1,
    sampler: 'euler',
    scheduler: 'beta',
    guidance: 3.0,
    removeBg: false,
  },

  // ── Inpaint settings ──
  inpaint: {
    prompt: '',
    inputImageB64: null,
    inputImageName: '',
    maskB64: null,
    brushSize: 40,
    brushMode: 'paint',
    steps: 28,
    // 1,0 : la zone est REFAITE. À 0,75 un quart de l'ancien contenu transparaissait
    // sous le masque (fantômes) et le nouveau restait contraint par l'ancien. Le fondu
    // avec l'entourage est assuré par le masque adouci (maskGrow / maskBlur) et
    // DifferentialDiffusion, pas par un denoise partiel. Pour une retouche légère qui
    // garde la structure, descendre à 0,5-0,6.
    denoise: 1.0,
    seed: -1,
    sampler: 'euler',
    scheduler: 'beta',
    guidance: 3.5,
    // FeatherMask (worker) adoucit les BORDS DE L'IMAGE, pas le contour peint : sans
    // effet utile, et il affaiblissait le masque près des bords. Laissé à 0.
    feathering: 0,
    // Marge et flou appliqués au masque AVANT l'envoi (MaskEditor.exportMask) : c'est
    // ce qui donne au modèle la place de fondre le nouveau contenu dans l'existant.
    maskGrow: 24,
    maskBlur: 12,
  },

  // ── Outpaint settings ──
  outpaint: {
    prompt: '',
    inputImageB64: null,
    inputImageName: '',
    padLeft: 0,
    padTop: 0,
    padRight: 256,
    padBottom: 0,
    // Ici le plumage agit bien sur la transition (ImagePadForOutpaint) : 96 px
    // au lieu de 80 pour une couture plus douce sur 256 px d'extension.
    feathering: 96,
    steps: 28,
    seed: -1,
    sampler: 'euler',
    scheduler: 'beta',
    guidance: 3.5,
  },

  // ── Rembg advanced settings (shared) ──
  rembgSettings: {
    model: 'LUCIDA',                  // LUCIDA (défaut) | BEN2 | RMBG-1.4 | Inspyrenet
    alphaMatting: false,               // Advanced: softer edges
    foregroundThreshold: 240,          // 0-255
    backgroundThreshold: 10,           // 0-255
    erodeSize: 10,                     // 0-255
    refineForeground: false,
    addBackground: 'none',             // none | white | black
  },

  // ── Post-generation rembg state ──
  resultOriginalB64: null,
  resultRembgB64: null,
  // Tracks job IDs already auto-saved to the gallery in this session.
  // Prevents duplicate saves when multiple useAIJobSocket instances observe
  // the same currentJobId (e.g. user switches tabs after a completion).
  handledJobIds: [],
  showRembgResult: false,

  // ── Cutout state ──
  cutout: {
    inputImageB64: null,
    inputImageName: '',
    resultB64: null,
    // ── File de détourage par lot ────────────────────────────────────────
    // Jusqu'à MAX_FICHIERS entrées, chacune menant sa propre vie :
    //   { id, nom, sourceB64, etat, jobId, resultatB64,
    //     erreur, galerieImageId, editeB64 }
    // `etat` : 'attente' | 'cours' | 'fait' | 'echec'
    //
    // La file NE PASSE PAS par `currentJobId` : ce champ ne suit qu'un seul
    // travail, et dix détourages s'y écraseraient les uns les autres. Chaque
    // entrée porte donc son propre identifiant de travail.
    fichiers: [],
    // Dossier de galerie créé pour CE lot. Les résultats bruts y sont
    // enregistrés automatiquement, et une version nettoyée à la gomme y
    // retourne — « le même dossier initial ».
    dossier: null,          // { id, name }
    lotEnCours: false,
    // Index du fichier ouvert dans l'éditeur de gomme, ou null.
    editionIndex: null,
  },

  // ── Vectorize state ──
  vectorize: {
    inputImageB64: null,
    mode: 'color',
    detailLevel: 6,
    colorPrecision: 6,
    resultSvg: null,
  },

  // ── Mockup (flacon e-liquide + étiquette) ──
  mockup: {
    inputImageB64: null,      // l'étiquette / visuel à apposer (data-URL)
    inputImageName: '',
    bottleSize: '60ml',       // 10ml | 30ml | 60ml | 75ml | 100ml
    material: 'transparent',  // transparent | black | custom
    bottleColor: '#3FA9F5',   // si material=custom
    capColor: 'black',        // transparent | black | custom
    capColorHex: '#222222',   // si capColor=custom
    showEliquid: false,       // afficher le sélecteur de couleur d'e-liquide
    eliquidColor: '#E0A93B',
    view: 'front',            // front | three_quarter | dynamic
    background: 'white',      // white | black | gray | custom
    backgroundDecor: '',      // texte libre si background=custom
    resolution: '1K',         // 1K | 2K | 4K
    resultB64: null,          // mockup généré
    resultW: 0,
    resultH: 0,
    rembgB64: null,           // version détourée (fond transparent)
    showCompare: false,       // slider avant/après
    generating: false,
    detourLoading: false,
    error: null,
  },

  // ── Gallery (full file browser) ──
  gallery: {
    images: [],
    folders: [],
    totalImages: 0,
    page: 1,
    pageSize: 24,
    loading: false,

    // View state
    viewMode: 'grid',        // 'grid' | 'list'
    thumbnailSize: 'medium', // 'small' | 'medium' | 'large'
    sortBy: 'created_at',
    sortOrder: 'desc',

    // Navigation
    currentFolderId: null,   // null = root
    breadcrumb: [],          // [{id, name}, ...]

    // Selection
    selectedIds: [],
    lastClickedId: null,

    // Filters
    searchQuery: '',
    workflowTypeFilter: null,
    favoritesOnly: false,

    // Preview
    previewImage: null,      // Full image object or null
  },

  // ═══════════════════════════════════════
  // Actions
  // ═══════════════════════════════════════

  // Panel
  togglePanel: () => set((s) => (s.isOpen ? { isOpen: false } : { isOpen: true, view: 'studio' })),
  openPanel: () => set({ isOpen: true, view: 'studio' }),
  closePanel: () => set({ isOpen: false }),
  // Ouvre le dock droit sur FDS Studio (même mécanique que le Studio I.A —
  // view 'fds', isOpen true → le dock affiche les onglets + le panneau FDS).
  openFds: () => set({ isOpen: true, view: 'fds' }),
  // Ouvre le dock droit sur le Guide (aide complète). Même mécanique : le dock
  // n'est qu'un contenant, la languette verticale fait passer d'une vue à
  // l'autre sans rien fermer. Un second appui referme, pour que le bouton « ? »
  // de la barre du haut se comporte comme un interrupteur.
  toggleHelp: () => set((s) => {
    if (!s.isOpen) return { isOpen: true, view: 'help' };
    if (s.view !== 'help') return { view: 'help' };
    return { isOpen: false };
  }),
  setActiveTab: (tab) => set({ activeTab: tab }),
  // Bascule la vue du dock droit sans fermer le Studio (onglets).
  setView: (view) => set({ view }),
  // Bouton central « Studio I.A » — toggle intelligent : ouvre si fermé,
  // ramène sur la vue Studio si on regardait autre chose (inspecteur/FDS),
  // ferme si on était déjà sur le Studio.
  pressStudioButton: () => set((s) => {
    if (!s.isOpen) return { isOpen: true, view: 'studio' };
    if (s.view !== 'studio') return { view: 'studio' };
    return { isOpen: false };
  }),
  // Bouton central « FDS Studio » — toggle : ouvre/ramène sur FDS, ferme si déjà dessus.
  pressFdsButton: () => set((s) => {
    if (!s.isOpen) return { isOpen: true, view: 'fds' };
    if (s.view !== 'fds') return { view: 'fds' };
    return { isOpen: false };
  }),

  // ── Job tracking ──
  setCurrentJob: (jobId) => set((s) => ({
    currentJobId: jobId,
    currentJobStatus: 'queued',
    currentProgress: 0,
    currentStatusLog: '',
    currentError: null,
    currentResultUrl: null,
    currentResultB64: null,
    currentResultTab: s.activeTab,  // Remember which tab started this job
    resultOriginalB64: null,
    resultRembgB64: null,
  })),
  updateJobProgress: (progress, statusLog) => set({
    currentProgress: progress,
    currentStatusLog: statusLog || get().currentStatusLog,
    currentJobStatus: 'running',
  }),
  // On écrivait ici un libellé de fin dans `currentStatusLog`, que personne
  // n'affichait : le seul lecteur (ProgressBar) se retire de l'écran dès que le
  // travail quitte 'queued'/'running'. L'écriture était donc invisible, et
  // laissait croire qu'un message de fin existait quelque part.
  setJobCompleted: (resultUrl) => set({
    currentJobStatus: 'completed',
    currentProgress: 1,
    currentResultUrl: resultUrl,
  }),
  setJobFailed: (error) => set({
    currentJobStatus: 'failed',
    currentError: error,
  }),
  setResultB64: (b64) => set({ currentResultB64: b64 }),
  // Atomic check-and-mark: returns true if this is the first call for this jobId
  // (i.e. the caller should proceed with saving). Returns false if already handled.
  markJobHandledOrFalse: (jobId) => {
    if (!jobId) return false;
    const current = useAIStudioStore.getState().handledJobIds;
    if (current.includes(jobId)) return false;
    set({ handledJobIds: [...current.slice(-49), jobId] }); // keep last 50
    return true;
  },
  resetJob: () => set({
    currentJobId: null, currentJobStatus: 'idle', currentProgress: 0,
    currentStatusLog: '', currentError: null,
    currentResultUrl: null, currentResultB64: null, currentResultTab: null,
    // Also clear comparator images so a new job doesn't inherit the previous rembg
    resultOriginalB64: null, resultRembgB64: null,
  }),
  // Optimistic job start: flip UI to "queued" immediately while the HTTP POST
  // to the backend is in flight (~500ms-2s). This avoids the dead zone where
  // the button looked active after the user clicked.
  beginJobSubmission: () => set((s) => ({
    currentJobId: null,
    currentJobStatus: 'queued',
    currentProgress: 0,
    currentStatusLog: 'Envoi de la requete...',
    currentError: null,
    currentResultUrl: null,
    currentResultB64: null,
    currentResultTab: s.activeTab,
    // Clear stale comparator images from any previous generation
    resultOriginalB64: null,
    resultRembgB64: null,
  })),

  // ── Tab-specific setters ──
  setInspiration: (p) => set((s) => ({ inspiration: { ...s.inspiration, ...p } })),
  setAnalyze: (p) => set((s) => ({ analyze: { ...s.analyze, ...p } })),
  setChat: (p) => set((s) => ({ chat: { ...s.chat, ...p } })),
  appendChatMessage: (msg) => set((s) => ({ chat: { ...s.chat, messages: [...s.chat.messages, msg] } })),
  updateChatMessage: (id, patch) => set((s) => ({
    chat: {
      ...s.chat,
      messages: s.chat.messages.map((m) => (m.id === id ? { ...m, ...patch } : m)),
    },
  })),
  // Atomic update of a single image within a message — reads the LATEST message
  // state from the store (avoids race conditions when multiple jobs complete at once).
  updateChatImage: (messageId, jobId, patch) => set((s) => ({
    chat: {
      ...s.chat,
      messages: s.chat.messages.map((m) => {
        if (m.id !== messageId || !m.images) return m;
        return {
          ...m,
          images: m.images.map((i) => (i.job_id === jobId ? { ...i, ...patch } : i)),
        };
      }),
    },
  })),
  // Nouvelle conversation : on vide les messages + les réfs, mais on GARDE les
  // préférences (imageCount, aspectRatio) qui sont des réglages persistants.
  resetChat: () => set((s) => ({ chat: { ...s.chat, messages: [], input: '', conversationImageCount: 0, isSending: false, refImages: [], previewCache: {}, lastQuickPreview: null } })),
  setChatPreview: (key, preview) => set((s) => ({
    chat: {
      ...s.chat,
      previewCache: { ...s.chat.previewCache, [key]: preview },
      // Track the most recent generation as the "anchor" for chained edits
      lastQuickPreview: preview?.image_b64 || s.chat.lastQuickPreview,
    },
  })),
  setI2i: (p) => set((s) => ({ i2i: { ...s.i2i, ...p } })),
  setInpaint: (p) => set((s) => ({ inpaint: { ...s.inpaint, ...p } })),
  setOutpaint: (p) => set((s) => ({ outpaint: { ...s.outpaint, ...p } })),
  setCutout: (p) => set((s) => ({ cutout: { ...s.cutout, ...p } })),

  // ── File de détourage ──────────────────────────────────────────────────
  ajouterFichiersCutout: (entrees) => set((s) => {
    const place = Math.max(0, MAX_FICHIERS_CUTOUT - s.cutout.fichiers.length);
    return { cutout: { ...s.cutout, fichiers: [...s.cutout.fichiers, ...entrees.slice(0, place)] } };
  }),
  // Mise à jour PAR IDENTIFIANT et non par index : un retrait pendant qu'un
  // détourage tourne décalerait les index, et la réponse d'un travail
  // atterrirait sur la mauvaise vignette.
  majFichierCutout: (id, patch) => set((s) => ({
    cutout: {
      ...s.cutout,
      fichiers: s.cutout.fichiers.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    },
  })),
  retirerFichierCutout: (id) => set((s) => ({
    cutout: { ...s.cutout, fichiers: s.cutout.fichiers.filter((f) => f.id !== id) },
  })),
  viderFichiersCutout: () => set((s) => ({
    cutout: { ...s.cutout, fichiers: [], dossier: null, lotEnCours: false, editionIndex: null },
  })),
  setVectorize: (p) => set((s) => ({ vectorize: { ...s.vectorize, ...p } })),
  setMockup: (p) => set((s) => ({ mockup: { ...s.mockup, ...p } })),
  setRembgSettings: (p) => set((s) => ({ rembgSettings: { ...s.rembgSettings, ...p } })),
  setResultOriginal: (b64) => set({ resultOriginalB64: b64 }),
  setResultRembg: (b64) => set({ resultRembgB64: b64 }),
  toggleShowRembg: () => set((s) => ({ showRembgResult: !s.showRembgResult })),

  // ── Gallery actions ──
  setGallery: (p) => set((s) => ({ gallery: { ...s.gallery, ...p } })),

  setGalleryViewMode: (mode) => set((s) => ({
    gallery: { ...s.gallery, viewMode: mode }
  })),
  setGalleryThumbnailSize: (size) => set((s) => ({
    gallery: { ...s.gallery, thumbnailSize: size }
  })),
  // Le tri ne remettait pas la pagination à zéro, contrairement à la recherche,
  // aux favoris et au filtre de type : changer le tri depuis la page 3 rechargeait
  // une page 3 du nouveau classement, sans rapport avec ce qu'on regardait.
  setGallerySort: (sortBy, sortOrder) => set((s) => ({
    gallery: { ...s.gallery, sortBy, sortOrder, page: 1 }
  })),

  navigateToFolder: (folderId, breadcrumb) => set((s) => ({
    gallery: {
      ...s.gallery,
      currentFolderId: folderId,
      breadcrumb: breadcrumb || [],
      selectedIds: [],
      page: 1,
    }
  })),

  selectGalleryItem: (id, { shift = false, ctrl = false } = {}) => {
    const { gallery } = get();
    const { selectedIds, lastClickedId, images, folders } = gallery;

    let newSelection;
    if (shift && lastClickedId) {
      // Range selection
      const allIds = [...folders.map(f => f.id), ...images.map(i => i.id)];
      const startIdx = allIds.indexOf(lastClickedId);
      const endIdx = allIds.indexOf(id);
      if (startIdx >= 0 && endIdx >= 0) {
        const [from, to] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
        newSelection = [...new Set([...selectedIds, ...allIds.slice(from, to + 1)])];
      } else {
        newSelection = [id];
      }
    } else if (ctrl) {
      // Toggle
      newSelection = selectedIds.includes(id)
        ? selectedIds.filter(x => x !== id)
        : [...selectedIds, id];
    } else {
      // Single select
      newSelection = [id];
    }

    set((s) => ({
      gallery: { ...s.gallery, selectedIds: newSelection, lastClickedId: id }
    }));
  },

  selectAllGalleryItems: () => set((s) => {
    const allIds = [...s.gallery.folders.map(f => f.id), ...s.gallery.images.map(i => i.id)];
    return { gallery: { ...s.gallery, selectedIds: allIds } };
  }),

  clearGallerySelection: () => set((s) => ({
    gallery: { ...s.gallery, selectedIds: [], lastClickedId: null }
  })),

  setGalleryPreview: (image) => set((s) => ({
    gallery: { ...s.gallery, previewImage: image }
  })),

  setGallerySearch: (q) => set((s) => ({
    gallery: { ...s.gallery, searchQuery: q, page: 1 }
  })),

  setGalleryFavoritesOnly: (v) => set((s) => ({
    gallery: { ...s.gallery, favoritesOnly: v, page: 1 }
  })),

  toggleGalleryFavorite: (id) => set((s) => ({
    gallery: {
      ...s.gallery,
      images: s.gallery.images.map(img =>
        img.id === id ? { ...img, is_favorite: !img.is_favorite } : img
      ),
    }
  })),
}), {
  name: 'labelforge-ai-studio',
  storage: chatStorage,
  // SEULE la conversation est persistée, et de façon nommée plutôt que par
  // exclusion : tout le reste de ce magasin décrit un travail en cours (un
  // `currentJobId`, une image téléversée dans un onglet, une page de galerie)
  // qui n'a pas de sens à la relecture, et une liste d'exclusions se serait
  // périmée au premier champ ajouté.
  partialize: (state) => ({
    chat: {
      messages: state.chat.messages,
      input: state.chat.input,
      conversationImageCount: state.chat.conversationImageCount,
      refImages: state.chat.refImages,
      imageCount: state.chat.imageCount,
      aspectRatio: state.chat.aspectRatio,
      hauteurMessages: state.chat.hauteurMessages,
      resolution: state.chat.resolution,
      moteur: state.chat.moteur,
      raisonnement: state.chat.raisonnement,
      cutoutBg: state.chat.cutoutBg,
      mode: state.chat.mode,
      previewCache: state.chat.previewCache,
      lastQuickPreview: state.chat.lastQuickPreview,
      // `isSending`, `remainingToday` et `maxPerDay` sont volontairement absents :
      // `isSending` relu à `true` bloquerait la zone de saisie sans qu'aucune
      // réponse ne soit en route, et le quota du jour se redemande au serveur
      // à chaque ouverture.
    },
  }),
  merge: (persisted, courant) => ({
    ...courant,
    ...persisted,
    chat: { ...courant.chat, ...(persisted?.chat || {}), isSending: false },
  }),
}));
